import React, {
	ComponentType,
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import store from 'store2';

import Modal, { ModalProps } from '@skippay/core/components/Modal/Modal';
import useLocation from '@skippay/core/hooks/useLocation';

interface ModalItem {
	Component: ComponentType<ModalProps>;
	props: ModalProps;
	priority: number;
	id: string;
	expires?: number;
	delay?: number;
}

interface ModalContextType {
	openModal: (
		id: ModalItem['id'],
		Component: ModalItem['Component'],
		props: ModalItem['props'],
		priority: ModalItem['priority'],
		expires?: ModalItem['expires'],
		delay?: ModalItem['expires']
	) => void;
	closeModal: (id?: string) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
	const context = useContext(ModalContext);
	if (!context) {
		throw new Error('useModal must be used within a ModalProvider');
	}
	return context;
};

interface ModalProviderProps {
	children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
	const [modals, setModals] = useState<ModalItem[]>([]);
	// isModalOpened - to prevent flashing modals on load
	const [isModalOpened, setIsModalOpened] = useState(false);
	const location = useLocation();

	const openModal = useCallback(
		(id, Component: ComponentType<ModalProps>, props: ModalProps = {}, priority = 0, expires) => {
			if (id) {
				const modalViewed = store.local.get(id);
				if (modalViewed && !expires) {
					return;
				}
				if (modalViewed?.expires && (!expires || modalViewed.expires > Date.now())) {
					return;
				}
			}

			setModals((currentModals) => [...currentModals, { Component, props, priority, id, expires }]);
		},
		[]
	);

	const highestPriorityModal = modals.length
		? modals.reduce(
				(highest, modal) => (modal.priority > highest.priority ? modal : highest),
				modals[0]
			)
		: null;

	const closeModal = useCallback(
		(id?: string) => {
			if (highestPriorityModal?.id && (!id || highestPriorityModal.id === id)) {
				store.local.set(highestPriorityModal.id, {
					value: true,
					expires: highestPriorityModal.expires
						? Date.now() + highestPriorityModal.expires
						: undefined,
				});

				setModals([]);
			}
		},
		[highestPriorityModal]
	);

	useEffect(() => {
		setIsModalOpened(false);
		const timer = setTimeout(() => setIsModalOpened(true), 2000);
		return () => clearTimeout(timer);
	}, [location.location.pathname]);

	return (
		<ModalContext.Provider value={{ openModal, closeModal }}>
			{children}
			{highestPriorityModal && (
				<Modal
					{...highestPriorityModal.props}
					isOpen={isModalOpened}
					onClose={() => {
						highestPriorityModal.props.onClose?.();
						closeModal();
					}}
				>
					{React.createElement(highestPriorityModal.Component, {
						...highestPriorityModal.props,
						onClose: () => {
							highestPriorityModal.props.onClose?.();
							closeModal();
						},
					})}
				</Modal>
			)}
		</ModalContext.Provider>
	);
};
